import React, { useState, useEffect } from "react";
import BASE_URL from "../../BASEURL";

export default function InquiriesList() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingInquiry, setDeletingInquiry] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchInquiries = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/inquiries`);
      if (!response.ok) {
        throw new Error('Failed to fetch inquiries');
      }
      const data = await response.json();
      setInquiries(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const handleDelete = (inquiry) => {
    setDeletingInquiry(inquiry);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingInquiry) return;

    try {
      const response = await fetch(`${BASE_URL}/api/inquiries/${deletingInquiry._id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete inquiry');
      }

      // Remove from local state
      setInquiries(inquiries.filter(inq => inq._id !== deletingInquiry._id));
      
      // Show success modal
      setSuccessMessage('Inquiry deleted successfully');
      setShowSuccessModal(true);
    } catch (err) {
      alert('Error deleting inquiry: ' + err.message);
    } finally {
      setShowDeleteModal(false);
      setDeletingInquiry(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingInquiry(null);
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage('');
  };

  if (loading) return (
    <div className="p-6 flex justify-center items-center min-h-screen">
      <div className="text-gold text-xl">Loading...</div>
    </div>
  );
  if (error) return (
    <div className="p-6 flex justify-center items-center min-h-screen">
      <div className="text-red-500 text-xl">Error: {error}</div>
    </div>
  );

  return (
    <div className="p-6 bg-light-grey">

      {/* PAGE TITLE */}
      <div className="flex flex-col gap-4 md:flex-row justify-between items-start md:items-center mb-8">
        <h1 className="text-3xl font-bold text-deep-black">
          Inquiries List
        </h1>
      </div>

      {/* TABLE VIEW FOR DESKTOP */}
      <div className="hidden md:block bg-white rounded-2xl shadow-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-deep-black text-white">
            <tr>
              <th className="px-6 py-4 text-left">Name</th>
              <th className="px-6 py-4 text-left">Email</th>
              <th className="px-6 py-4 text-left">Subject</th>
              <th className="px-6 py-4 text-left">Message</th>
              <th className="px-6 py-4 text-left">Created Date</th>
              <th className="px-6 py-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {inquiries.map((inq) => (
              <tr key={inq._id} className="border-b border-gray-200 hover:bg-light-grey transition-colors">
                <td className="px-6 py-4 font-semibold text-deep-black">{inq.name}</td>
                <td className="px-6 py-4 text-gray-600">{inq.email}</td>
                <td className="px-6 py-4 text-gray-600">{inq.subject}</td>
                <td className="px-6 py-4 text-gray-600 max-w-xs truncate">{inq.message}</td>
                <td className="px-6 py-4 text-gray-600">{new Date(inq.createdAt).toLocaleDateString()}</td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => handleDelete(inq)}
                    className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-md text-sm transition-colors"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MOBILE CARD VIEW */}
      <div className="md:hidden grid grid-cols-1 gap-6">
        {inquiries.map((inq) => (
          <div
            key={inq._id}
            className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold text-deep-black mb-2">{inq.name}</h3>
              <p className="text-gray-600 mb-1"><strong>Email:</strong> {inq.email}</p>
              <p className="text-gray-600 mb-1"><strong>Subject:</strong> {inq.subject}</p>
              <p className="text-gray-600 mb-1"><strong>Message:</strong> {inq.message}</p>
              <p className="text-gray-600 mb-3"><strong>Created:</strong> {new Date(inq.createdAt).toLocaleDateString()}</p>
              <button
                onClick={() => handleDelete(inq)}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md text-sm transition-colors w-full"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && deletingInquiry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-deep-black mb-2">Delete Inquiry</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete the inquiry from <strong>{deletingInquiry.name}</strong>? 
                This action cannot be undone.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={cancelDelete}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-deep-black mb-2">Success!</h3>
              <p className="text-gray-600 mb-6">
                {successMessage}
              </p>
              <button
                onClick={closeSuccessModal}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}